import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { ShopifyAuthentication as ShopifyAuthType } from '@/types';
import { apiService } from '@/utils/api';

const ShopifyAuthentication: React.FC = () => {
  const [formData, setFormData] = useState({
    store_domain: '',
  });
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
  const [showTokenClearedSuccess, setShowTokenClearedSuccess] = useState(false);

  const queryClient = useQueryClient();
  const { data: sites = [] } = useQuery('sites', apiService.getSites);

  // Fetch existing Shopify authentication on load
  const { data: authData, isLoading } = useQuery<ShopifyAuthType>(
    'shopifyAuthentication',
    apiService.getShopifyAuthentication,
    {
      retry: 1,
      onError: () => {
        // If none exists (404 etc), keep empty form and fallback site selection
      },
    }
  );

  // Handle form data population when authData changes
  useEffect(() => {
    if (authData && authData.id !== undefined && authData.id !== null) {
      setFormData({
        store_domain: authData.store_domain || '',
      });
    } else {
      setFormData({
        store_domain: '',
      });
    }
  }, [authData]);

  useEffect(() => {
    if (authData?.site_id) {
      setSelectedSiteId(authData.site_id);
    } else if (sites.length > 0) {
      setSelectedSiteId((prev) => (prev === null ? sites[0].id : prev));
    }
  }, [authData, sites]);

  // Create/Update mutation
  const saveMutation = useMutation(
    (data: ShopifyAuthType) => {
      if (authData && authData.id !== undefined && authData.id !== null) {
        return apiService.updateShopifyAuthentication(data);
      } else {
        return apiService.createShopifyAuthentication(data);
      }
    },
    {
      onSuccess: () => {
        setShowSuccess(true);
        queryClient.invalidateQueries('shopifyAuthentication');
        setTimeout(() => setShowSuccess(false), 3000);
      },
      onError: () => {
        setShowError(true);
        setTimeout(() => setShowError(false), 3000);
      }
    }
  );

  // Delete mutation
  const deleteMutation = useMutation(
    () => apiService.deleteShopifyAuthentication(),
    {
      onSuccess: () => {
        setShowDeleteSuccess(true);
        setFormData({
          store_domain: '',
        });
        queryClient.invalidateQueries('shopifyAuthentication');
        setTimeout(() => setShowDeleteSuccess(false), 3000);
      },
      onError: () => {
        setShowError(true);
        setTimeout(() => setShowError(false), 3000);
      },
    }
  );

  const clearApiTokenMutation = useMutation(
    () => apiService.clearApiToken(),
    {
      onSuccess: () => {
        setShowTokenClearedSuccess(true);
        setTimeout(() => setShowTokenClearedSuccess(false), 3000);
      },
      onError: () => {
        setShowError(true);
        setTimeout(() => setShowError(false), 3000);
      },
    }
  );


  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const siteId = authData?.site_id ?? selectedSiteId;
    if (!siteId) {
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
      return;
    }

    const submitData: ShopifyAuthType = {
      ...formData,
      site_id: siteId,
      user_id: authData?.user_id ?? 0,
    };
    saveMutation.mutate(submitData);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete these settings?')) {
      deleteMutation.mutate();
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">Loading...</h3>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Shopify Authentication</h1>

      {/* Success Messages */}
      {showSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">
                Shopify Authentication Settings saved successfully
              </h3>
            </div>
          </div>
        </div>
      )}

      {showDeleteSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">
                Shopify Authentication Settings deleted successfully
              </h3>
            </div>
          </div>
        </div>
      )}

      {showTokenClearedSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">
                API token removed successfully
              </h3>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {showError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Something went wrong. Please try again.
              </h3>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow rounded-lg">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">

          {/* Store domain */}
          <div>
            <label htmlFor="store_domain" className="block text-sm font-medium text-gray-700 mb-2">
              Store domain
            </label>
            <input
              type="text"
              id="store_domain"
              name="store_domain"
              value={formData.store_domain}
              onChange={handleInputChange}
              placeholder="https://your-store.myshopify.com"
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              required
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end pt-6 space-x-3">
            <button
              type="submit"
              disabled={saveMutation.isLoading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50"
              style={{ backgroundColor: 'hsl(15 100% 52%)' }}
            >
              {saveMutation.isLoading ? 'Saving...' : 'Save Shopify Settings'}
            </button>

            {authData && authData.id !== undefined && authData.id !== null && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteMutation.isLoading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
              >
                {deleteMutation.isLoading ? 'Deleting...' : 'Delete'}
              </button>
            )}

            <button
              type="button"
              onClick={() => clearApiTokenMutation.mutate()}
              disabled={clearApiTokenMutation.isLoading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-gray-700 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50"
            >
              {clearApiTokenMutation.isLoading ? 'Removing...' : 'Remove api token'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ShopifyAuthentication;

